/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { isEmpty } from 'lodash'

import type { BillingSubscriptionLevel, UserProfile, ProfileState } from '@modules/ui-types'
import type { BillingActivity, Subscription } from '@modules/ui-types/billing'
import type { PaymentDetails, LineItem } from '@modules/billing-api/customTypes'

import { DEFAULT_SUBSCRIPTION_LEVEL, SUBSCRIPTION_LEVELS } from './constants'

interface BillingSubscriptionWithHourlyRate extends Subscription {
  hourlyRate: number
}

export function getSubscriptionsWithHourlyRates({
  subscription,
  activity,
}: {
  subscription: Subscription
  activity?: BillingActivity
}): BillingSubscriptionWithHourlyRate {
  const hourlyRate = activity?.[subscription.value] || 0

  return {
    ...subscription,
    hourlyRate,
  }
}
export const getHourlyRateForLevel = ({
  subscription,
  activity,
}: {
  subscription: BillingSubscriptionLevel
  activity?: BillingActivity
}) => activity?.[subscription] || 0

export const getSubscriptionQuery = (level: string | null | undefined): BillingSubscriptionLevel =>
  SUBSCRIPTION_LEVELS.find((each) => each === level) ?? DEFAULT_SUBSCRIPTION_LEVEL

export function isTrialUser(profile: UserProfile): boolean {
  return profile.inTrial
}

export function isTrialNotStartedUser({ inTrial, trials }: UserProfile): boolean {
  return inTrial && !trials.length
}

export function isNotPayingUser(profile: UserProfile, paymentDetails?: PaymentDetails): boolean {
  return (
    !profile.invoicable &&
    !profile.is_paying &&
    isEmpty(paymentDetails?.recurly_billing_info) &&
    paymentDetails?.external_billing_info?.card == null
  )
}

export function isTrialOrNotPayingUser(
  profile: UserProfile,
  paymentDetails?: PaymentDetails,
): boolean {
  if (isTrialUser(profile)) {
    // always show for trial users
    return true
  }

  // if a user is not in trial and is not yet paying (for whatever reason), we want to show this
  // but only if they don't already have CC details added
  return isNotPayingUser(profile, paymentDetails)
}

export function isActiveItem({ active }: LineItem): boolean {
  return active
}

export function isExpiredItem(lineItem: LineItem): boolean {
  return !lineItem.active && !isFutureItem(lineItem)
}

export function isFutureItem({ start, active }: LineItem): boolean {
  return !active && new Date(start) > new Date()
}

export function getActiveItems(items: LineItem[]): LineItem[] {
  return items.filter((item) => isActiveItem(item))
}

export function getExpiredItems(items: LineItem[]): LineItem[] {
  return items.filter((item) => isExpiredItem(item))
}

export function getFutureItems(items: LineItem[]): LineItem[] {
  return items.filter((item) => isFutureItem(item))
}

export const defaultSubscriptionLevelByUser = ({
  profile,
  trialDefaultTierFlag,
}: {
  profile: ProfileState | UserProfile
  trialDefaultTierFlag?: BillingSubscriptionLevel
}): BillingSubscriptionLevel => {
  if (!profile || !profile.inTrial) {
    return DEFAULT_SUBSCRIPTION_LEVEL
  }

  if (trialDefaultTierFlag && SUBSCRIPTION_LEVELS.includes(trialDefaultTierFlag)) {
    return trialDefaultTierFlag
  }

  return DEFAULT_SUBSCRIPTION_LEVEL
}

export const getSubscriptionChangeEventType = (
  level: BillingSubscriptionLevel,
  pending_level: BillingSubscriptionLevel,
): 'upgrade' | 'downgrade' => {
  if (SUBSCRIPTION_LEVELS.indexOf(level) < SUBSCRIPTION_LEVELS.indexOf(pending_level)) {
    return 'upgrade'
  }

  return 'downgrade'
}
